import { useMemo } from 'react';

import { useCompactOrderHistory } from '@/components/Homepage/HistorySection/useHistorySectionData';

import { useTradeHistoryTokenResults } from '@/hooks/tokenQueries/useTradeHistoryTokenResults';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';

import { getTradeIdentity } from '@/utils/0x/history/status';
import type { MatchaTradeHistoryEntry } from '@/utils/0x/history/types';
import type { TokenBase } from '@/utils/0x/token-registry.types';
import { tokenKey } from '@/utils/normalizeAddress';

const RECENT_TRADES_LIMIT = 10;
const NO_TRADES: MatchaTradeHistoryEntry[] = [];

/** Tokens from the connected wallets' latest settled trades, newest first and deduped. */
export function useRecentlyTradedTokens({
  chainId,
  enabled = true,
}: {
  chainId?: number;
  enabled?: boolean;
}): { tokens: TokenBase[]; isLoading: boolean } {
  const { ethereumWallet, solanaWallet } = useMatchaWallets();
  const historyArgs = {
    scopeTokens: undefined,
    scopeReady: true,
    limit: RECENT_TRADES_LIMIT,
    page: 1,
    enabled,
  };
  const ethereumHistory = useCompactOrderHistory({
    ...historyArgs,
    address: ethereumWallet?.address,
  });
  const solanaHistory = useCompactOrderHistory({ ...historyArgs, address: solanaWallet?.address });

  // `rows` is a fresh slice every render; key on trade identities so the
  // token lookups and consumers' memos only rerun when the trades change.
  const rows = [...ethereumHistory.rows, ...solanaHistory.rows];
  const rowsSignature = rows.map(getTradeIdentity).join('|');
  const trades = useMemo(
    () => rows.sort((a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowsSignature],
  );
  // Cached or locally merged history can still yield rows while disabled, so
  // gate the metadata lookups here too.
  const { getTradeTokenResults } = useTradeHistoryTokenResults(enabled ? trades : NO_TRADES);

  const tokens = useMemo(() => {
    const seen = new Set<string>();
    const result: TokenBase[] = [];
    for (const trade of trades) {
      const { takerTokenResult, makerTokenResult } = getTradeTokenResults(trade);
      for (const token of [takerTokenResult, makerTokenResult]) {
        if (!token || (chainId !== undefined && token.chainId !== chainId)) continue;
        const key = tokenKey(token.address, token.chainId);
        if (seen.has(key)) continue;
        seen.add(key);
        result.push(token);
      }
    }
    return result;
  }, [trades, getTradeTokenResults, chainId]);

  return { tokens, isLoading: ethereumHistory.isLoading || solanaHistory.isLoading };
}
